import { useEffect, useState, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { showWarningAlert } from '../utils/alert';
import { haversineDistance } from '../utils/utils';
import { createInfoCard } from './InfoCard';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const COINS = [
  { id: 1, lat: 31.5204, lng: 74.3587 },
  { id: 2, lat: 33.6844, lng: 73.0479 },
  { id: 3, lat: 31.660101, lng: 73.935246 },
  { id: 4, lat: 31.420211, lng: 74.24318 },
  { id: 5, lat: 31.660054, lng: 73.935277 },
  { id: 6, lat: 31.5654144, lng: 74.3571456 },
  { id: 7, lat: 31.559992487574895, lng: 74.39599295296996 },
  { id: 8, lat: 30.9723136, lng: 73.9704832 },
  { id: 9, lat: 31.5293698, lng: 74.3243778 },
  { id: 10, lat: 31.506432, lng: 74.3374848 },
  { id: 11, lat: 31.47093, lng: 74.30472 },
  { id: 12, lat: 31.4602862, lng: 74.3235425 },
  { id: 13, lat: 31.4933248, lng: 74.3112704 },
  { id: 14, lat: 31.4933248, lng: 74.3112707 }
];

const drawRoute = async (map, start, end, profile = 'driving') => {
  try {
    const query = await fetch(
      `https://api.mapbox.com/directions/v5/mapbox/${profile}/${start.longitude},${start.latitude};${end.longitude},${end.latitude}?geometries=geojson&access_token=${mapboxgl.accessToken}`
    );
    const data = await query.json();
    const route = data.routes[0].geometry;

    const geojson = { type: 'Feature', geometry: route };

    if (map.getSource('route')) {
      map.getSource('route').setData(geojson);
    } else {
      map.addSource('route', { type: 'geojson', data: geojson });
      map.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': 'red', 'line-width': 5 },
      });
    }
  } catch (err) {
    console.error('Route error:', err);
  }
};

export default function CoinMap({ onEnterAR }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const userMarker = useRef(null);
  const lastRouteUpdateRef = useRef(Date.now());
  const [userLocation, setUserLocation] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const enterFullscreen = () => {
    const mapContainerEl = mapContainer.current;
    if (mapContainerEl?.requestFullscreen) {
      mapContainerEl.requestFullscreen().catch(err => console.error(err));
    }
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
  };

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const initialLoc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setUserLocation(initialLoc);

        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [initialLoc.longitude, initialLoc.latitude],
          zoom: 15,
        });
        map.current.addControl(new mapboxgl.NavigationControl(), 'top-left');

        // Add GeolocateControl to track user location automatically
        const geolocateControl = new mapboxgl.GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
          trackUserLocation: true,
          showAccuracyCircle: false,
          showUserHeading: true,
        });
        map.current.addControl(geolocateControl, 'top-left');
        geolocateControl.trigger();

        // Custom user marker
        const userEl = document.createElement('div');
        userEl.className = 'w-10 h-10 rounded-full border-2 border-white shadow-lg';
        userEl.style.backgroundImage = "url('/user.png')";
        userEl.style.backgroundSize = 'cover';
        userMarker.current = new mapboxgl.Marker(userEl).setLngLat([initialLoc.longitude, initialLoc.latitude]).addTo(map.current);

        const infoCard = createInfoCard({
          title: 'You',
          subtitle: 'Current Location',
          onModeSelect: (mode) => {
            enterFullscreen();
            const DESTINATION = { latitude: 31.4602862, longitude: 74.3235425 };
            drawRoute(map.current, initialLoc, DESTINATION, mode);
          },
        });

        const popup = new mapboxgl.Popup({ offset: 25 }).setDOMContent(infoCard);
        userMarker.current.setPopup(popup).togglePopup();

        // Add coin markers
        COINS.forEach((coin) => {
          const marker = new mapboxgl.Marker().setLngLat([coin.lng, coin.lat]).addTo(map.current);
          const el = marker.getElement();
          el.style.fontSize = '24px';
          el.style.cursor = 'pointer';
          el.innerHTML = '🪙';
          el.addEventListener('click', () => {
            if (!userLocation) return;
            const dist = haversineDistance(userLocation, { latitude: coin.lat, longitude: coin.lng });
            if (dist < 500) onEnterAR(coin);
            else showWarningAlert('📍You are too far from the coin.', 'Got it');
          });
        });

        // Update user marker on location update
        geolocateControl.on('geolocate', (evt) => {
          const newLoc = { latitude: evt.coords.latitude, longitude: evt.coords.longitude };
          setUserLocation(newLoc);
          userMarker.current?.setLngLat([newLoc.longitude, newLoc.latitude]);

          // Throttle route updates
          const now = Date.now();
          if (now - lastRouteUpdateRef.current > 3000) {
            lastRouteUpdateRef.current = now;
            const DESTINATION = { latitude: 31.4602862, longitude: 74.3235425 };
            drawRoute(map.current, newLoc, DESTINATION);
          }
        });
      },
      (err) => {
        console.error('Geolocation error:', err);
        setUserLocation({ latitude: 31.5204, longitude: 74.3587 });
      }
    );
  }, []);

  return (
    <div className="relative h-screen">
      <div ref={mapContainer} className="h-full relative">
        {isFullscreen && (
          <button
            onClick={exitFullscreen}
            className="absolute top-4 left-4 bg-white px-4 py-2 rounded-lg shadow-lg z-[9999]"
          >
            🔙 Back
          </button>
        )}
      </div>
      <button
        onClick={() => {
          if (!userLocation) {
            showWarningAlert('📍 Location not available.', 'OK');
            return;
          }
          let closestCoin = null;
          let closestDist = Infinity;
          COINS.forEach((coin) => {
            const dist = haversineDistance(userLocation, { latitude: coin.lat, longitude: coin.lng });
            if (dist < closestDist) {
              closestDist = dist;
              closestCoin = coin;
            }
          });
          if (closestCoin && closestDist < 500) onEnterAR(closestCoin);
          else showWarningAlert('📍You are too far from any coin.', 'Got it');
        }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 px-5 py-3 bg-blue-600 text-white rounded-lg font-bold z-10 shadow-md text-xl"
      >
        🎯 Enter AR View
      </button>
    </div>
  );
}
